import { useState } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Input, Label, FormGroup, Button, Badge, Alert } from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useAvailableDedicated,
  useCustomerDedicatedDevices,
  useReserveDedicated,
  useReleaseDedicated,
} from './useNexusDedicated';

/**
 * Dedicated-Server-Verwaltung (N3 NetBox-Routing).
 *
 * Zwei Flows: (1) freies Device einer Rolle finden + einem Kunden
 * zuweisen, (2) alle Devices anzeigen, die einem Kunden bereits dediziert
 * zugeordnet sind, mit Freigabe-Option.
 */
export function NexusDedicatedPage() {
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const [role, setRole] = useState('enhance-webserver');
  const [site, setSite] = useState('');
  const [assignCustomerId, setAssignCustomerId] = useState('');
  const [lookupCustomerId, setLookupCustomerId] = useState('');

  const available = useAvailableDedicated({ role, site });
  const customerDevices = useCustomerDedicatedDevices(lookupCustomerId || null);
  const reserve = useReserveDedicated();
  const release = useReleaseDedicated();

  const device = available.data?.device ?? null;
  const devices = customerDevices.data?.devices ?? [];

  return (
    <div>
      <PageHeader title="Nexus · Dedicated-Server" subtitle="NetBox-Server-Zuordnung pro Kunde" />
      <Alert color="info" className="small">
        Dedizierte Server werden in NetBox als Custom-Field
        (<code>dedicated_to_customer_id</code>) geführt. Enhance-Provisioning
        routet automatisch dorthin, sobald ein Kunde ein Device zugewiesen
        bekommt — siehe <code>apps/nexus/docs/NETBOX-DEDICATED.md</code>.
      </Alert>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">Freies Device finden + zuweisen</CardTitle>
              <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
                <FormGroup className="mb-0">
                  <Label className="small text-muted mb-1">Rolle</Label>
                  <Input type="text" bsSize="sm" value={role} onChange={(e) => setRole(e.target.value)} style={{ minWidth: 200 }} />
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label className="small text-muted mb-1">Site (optional)</Label>
                  <Input type="text" bsSize="sm" value={site} onChange={(e) => setSite(e.target.value)} placeholder="z.B. fra1" style={{ minWidth: 140 }} />
                </FormGroup>
                <Button size="sm" color="secondary" outline onClick={() => available.refetch()}>Suchen</Button>
              </div>

              {available.isLoading && <div className="text-muted small">Suche läuft…</div>}
              {available.error && <Alert color="danger" className="small">{available.error.message}</Alert>}
              {!available.isLoading && !device && (
                <div className="text-muted small">Kein freies Device für diese Rolle gefunden.</div>
              )}
              {device && (
                <div className="border rounded p-2 mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{device.name}</strong> <span className="text-muted small">#{device.id}</span>
                      <div className="small text-muted">{device.role_name} · {device.site} · {device.primary_ip}</div>
                    </div>
                    <Badge color="success" pill>frei</Badge>
                  </div>
                  {canWrite && (
                    <div className="d-flex gap-2 align-items-end mt-2">
                      <FormGroup className="mb-0">
                        <Label className="small text-muted mb-1">Customer-ID</Label>
                        <Input type="number" bsSize="sm" value={assignCustomerId} onChange={(e) => setAssignCustomerId(e.target.value)} style={{ width: 120 }} />
                      </FormGroup>
                      <Button
                        size="sm"
                        color="primary"
                        disabled={!assignCustomerId || reserve.isPending}
                        onClick={() => reserve.mutateAsync({ deviceId: device.id, customerId: Number(assignCustomerId) })}
                      >
                        Zuweisen
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <CardBody>
              <CardTitle tag="h6">Devices eines Kunden</CardTitle>
              <div className="d-flex gap-2 align-items-end mb-3">
                <FormGroup className="mb-0">
                  <Label className="small text-muted mb-1">Customer-ID</Label>
                  <Input type="number" bsSize="sm" value={lookupCustomerId} onChange={(e) => setLookupCustomerId(e.target.value)} style={{ width: 140 }} />
                </FormGroup>
                <Button size="sm" color="secondary" outline onClick={() => customerDevices.refetch()}>Laden</Button>
              </div>

              <DataTable
                loading={customerDevices.isFetching}
                error={customerDevices.error}
                empty={!!lookupCustomerId && !customerDevices.isFetching && devices.length === 0}
                emptyMessage="Diesem Kunden ist kein Device dediziert."
              >
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Rolle</th>
                    <th>IP</th>
                    <th className="text-end">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id}>
                      <td>{d.name} <span className="text-muted small">#{d.id}</span></td>
                      <td className="small">{d.role_name ?? d.role}</td>
                      <td className="small font-monospace">{d.primary_ip ?? '—'}</td>
                      <td className="text-end">
                        {canWrite && (
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            disabled={release.isPending}
                            onClick={() => release.mutateAsync({ deviceId: d.id })}
                          >
                            Freigeben
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
